import { useState, useEffect } from "react";
import { format } from "date-fns";
import { DataGrid, IconButton, Loading, Tooltip } from "../../../components";
import {
  history,
  OrcamentoServicosService,
  notification,
  PessoaService,
} from "../../../services";
import { formatDate, formatPrice, responseErros } from "../../../utils";
import { FormFiltroAvancado } from "./components";
import { PermisoesHelper } from "../../../helpers";
import { ListLayout } from "../../../layouts";

const orcamentoServicosService = new OrcamentoServicosService();
const pessoaService = new PessoaService();

const ListOrcamentoServico = () => {
  const [loading, setLoading] = useState(false);
  const [orcamentosServico, setOrcamentosServico] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [listaPessoas, setListaPessoas] = useState([]);
  const permissoesHelper = new PermisoesHelper();
  permissoesHelper.validarPermisao("orcamento-servicos-visualizar");

  const status = [
    {
      nome: "ABERTO",
      label: "Aberto",
      cor: "#4FBB53",
    },
    {
      nome: "IMPORTADO",
      label: "Importado",
      cor: "#004780",
    },
    {
      nome: "CANCELADO",
      label: "Cancelado",
      cor: "#DC3545",
    },
  ];

  const colunas = [
    {
      field: "status",
      headerName: "Status",
      sortable: true,
      flex: 50,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        switch (params.value) {
          case "ABERTO":
            return (
              <Tooltip title="Aberto" arrow placement="right">
                <i
                  className="ph-fill ph-circle"
                  style={{ color: "#4FBB53", fontSize: 18 }}
                ></i>
              </Tooltip>
            );
          case "IMPORTADO":
            return (
              <Tooltip title="Importado" arrow placement="right">
                <i
                  className="ph-fill ph-circle"
                  style={{ color: "#004780", fontSize: 18 }}
                ></i>
              </Tooltip>
            );
          case "CANCELADO":
            return (
              <Tooltip title="Cancelado" arrow placement="right">
                <i
                  className="ph-fill ph-circle"
                  style={{ color: "#DC3545", fontSize: 18 }}
                ></i>
              </Tooltip>
            );
          default:
            return;
        }
      },
    },
    {
      field: "numeroDocumento",
      headerName: "Nº Documento",
      flex: 100,
      align: "center",
      headerAlign: "center",
      filtrar: true,
    },
    {
      field: "clienteId",
      headerName: "Cliente",
      width: 500,
      sortable: false,
      valueGetter: (params) =>
        listaPessoas.find((item) => item.id == params.row.clienteId)
          ?.nomeRazaoSocial,
    },
    {
      field: "dataVencimento",
      headerName: "Data Vencimento",
      flex: 100,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "createdAt",
      headerName: "Data Lançamento",
      flex: 100,
      align: "center",
      headerAlign: "center",
      valueGetter: (params) =>
        format(formatDate.received(params.value), "dd/MM/yyyy"),
    },
    {
      field: "totalOrdemServico",
      headerName: "Valor",
      align: "right",
      headerAlign: "right",
      flex: 150,
      sortable: false,
      valueGetter: (params) => formatPrice(params.value, false),
    },
    {
      field: "acoes",
      headerName: "Ações",
      sortable: false,
      flex: 100,
      align: "center",
      headerAlign: "center",
      disableClickEventBubbling: true,
      renderCell: (params) => {
        return (
          <div className="d-flex align-items-center">
            {permissoesHelper.temPermisao("orcamento-servicos-editar") && (
              <IconButton
                onClick={() => {
                  onClickEditOrcamentoServico(params.id);
                }}
              >
                <Tooltip title="Editar" arrow placement="right">
                  <i className="ph-fill ph-pencil" style={{ fontSize: 18 }}></i>
                </Tooltip>
              </IconButton>
            )}
            {permissoesHelper.temPermisao("orcamento-servicos-cancelar") &&
              params.row.status === "ABERTO" && (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onClickCancelarOrcamentoServico(params.id);
                  }}
                >
                  <Tooltip title="Cancelar" arrow placement="right">
                    <i
                      className="ph-fill ph-prohibit"
                      style={{ fontSize: 18 }}
                    ></i>
                  </Tooltip>
                </IconButton>
              )}
            {permissoesHelper.temPermisao("orcamento-servicos-excluir") &&
              params.row.status === "ABERTO" && (
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onClickDeleteOrcamentoServico(params.id);
                  }}
                >
                  <Tooltip title="Excluir" arrow placement="right">
                    <i
                      className="ph-fill ph-trash"
                      style={{ fontSize: 18 }}
                    ></i>
                  </Tooltip>
                </IconButton>
              )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    buscarOrcamentosServico();
  }, []);

  const buscarOrcamentosServico = async function (filtro) {
    setLoading(true);
    const result = await orcamentoServicosService.getAll(filtro);
    if (!result.isAxiosError) {
      setOrcamentosServico(result.data.rows);
      setRowCount(result.data.count);
      buscarRelacionamentos(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const buscarRelacionamentos = (listaDocumento) => {
    const idsPessoas = [
      ...new Set(
        listaDocumento
          .map(({ clienteId }) => (clienteId ? clienteId : 0))
          .filter(
            (clienteId, index, listaDocumento) =>
              listaDocumento.indexOf(clienteId) === index,
          ),
      ),
    ];
    buscarPessoas(idsPessoas);
  };

  const buscarPessoas = (listaIds) => {
    if (listaIds.length) {
      const filtro = {
        id: listaIds,
        nonPaginated: true,
        attributes: ["id", "nomeRazaoSocial"],
        disabledInclude: true,
      };
      pessoaService.getAll(filtro).then((result) => {
        if (!result.isAxiosError) {
          setListaPessoas(result.data);
        } else {
          responseErros(result);
        }
      });
    }
  };

  const onClickCadastrarOrcamentoServico = function () {
    history.push("/faturamento/orcamento-servicos/create");
  };

  const onClickEditOrcamentoServico = function (id) {
    history.push(`/faturamento/orcamento-servicos/${id}`);
  };

  const onClickCancelarOrcamentoServico = async function (id) {
    const retornoAlerta = await notification.confirmacao(
      "Cancelar!",
      "Tem certeza que deseja cancelar esse orçamento de serviço?",
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await orcamentoServicosService.cancelar(id);
      if (!result.isAxiosError) {
        buscarOrcamentosServico();
        notification.alteracaoSucesso();
      } else {
        setLoading(false);
        responseErros(result);
      }
    }
  };

  const onClickDeleteOrcamentoServico = async function (id) {
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      "Tem certeza que deseja excluir esse orçamento de serviço?",
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await orcamentoServicosService.deletar(id);
      if (!result.isAxiosError) {
        buscarOrcamentosServico();
        notification.deletadoSucesso();
      } else {
        setLoading(false);
        responseErros(result);
      }
    }
  };

  const buscarPesquisaAvancada = async (filter) => {
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filter,
    };
    if (
      filtrosAvancadosTemp.dataCreatedAtStart &&
      filtrosAvancadosTemp.dataCreatedAtEnd
    ) {
      porIntervalo.push({
        coluna: "createdAt",
        de: filtrosAvancadosTemp.dataCreatedAtStart,
        ate: filtrosAvancadosTemp.dataCreatedAtEnd,
      });
    }
    if (
      filtrosAvancadosTemp.dataVencimentoStart &&
      filtrosAvancadosTemp.dataVencimentoEnd
    ) {
      porIntervalo.push({
        coluna: "dataVencimento",
        de: filtrosAvancadosTemp.dataVencimentoStart,
        ate: filtrosAvancadosTemp.dataVencimentoEnd,
      });
    }
    delete filtrosAvancadosTemp.dataCreatedAtStart;
    delete filtrosAvancadosTemp.dataCreatedAtEnd;
    delete filtrosAvancadosTemp.dataVencimentoStart;
    delete filtrosAvancadosTemp.dataVencimentoEnd;

    setLoading(true);
    const result = await orcamentoServicosService.getAllFiltroAvancado({
      ...filtrosAvancadosTemp,
      restritiva: true,
      porIntervalo,
    });
    if (!result.isAxiosError) {
      setOrcamentosServico(result.data.rows);
      setRowCount(result.data.count);
      buscarRelacionamentos(result.data.rows);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const sendServerDatagrid = (props) => {
    if (props.tipoFiltro == "simples") {
      buscarOrcamentosServico(props.filtros);
    } else {
      buscarPesquisaAvancada(props.filtros);
    }
  };

  return (
    <ListLayout
      title="Orçamento de Serviço"
      onClickCadastrar={onClickCadastrarOrcamentoServico}
      permissaoButton={permissoesHelper.temPermisao(
        "orcamento-servicos-cadastrar",
      )}
    >
      <DataGrid
        rows={orcamentosServico}
        rowCount={rowCount}
        columns={colunas}
        onClickEdit={onClickEditOrcamentoServico}
        onClickDelete={onClickDeleteOrcamentoServico}
        paginationMode={"server"}
        visualizarShow={permissoesHelper.temPermisao(
          "orcamento-servicos-visualizar",
        )}
        editShow={permissoesHelper.temPermisao("orcamento-servicos-editar")}
        deleteShow={permissoesHelper.temPermisao("orcamento-servicos-excluir")}
        sendServer={(props) => sendServerDatagrid(props)}
        FormFiltroAvancado={FormFiltroAvancado}
        listaStatus={status}
        disableAcoes
        enableRowDoubleClick
      />
      <Loading loading={loading} />
    </ListLayout>
  );
};

export default ListOrcamentoServico;
